import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  Suspense,
} from 'react';
import { useRouter } from 'found';
import { Box, Container, useScrollTrigger, NoSsr } from '@mui/material';
import CommunitiesIcon from '@mui/icons-material/Group';
import EventsIcon from '@mui/icons-material/Event';
import ProductsIcon from '@mui/icons-material/ShoppingCart';
import Helmet from 'react-helmet';
import strip from 'strip';
import LoadBlur from 'components/LoadBlur';
import {
  Assignment,
  ExitToApp,
  Explore,
  Flag,
  // Loyalty,
  Notifications,
  Person,
} from '@mui/icons-material';
import { useAuthContext } from 'AuthContext';
import store from 'lib/store';
import AcceptTermsDialog from 'containers/Page/AcceptTermsDialog';
import tracker from 'lib/tracker';
import BottomNav from 'containers/Page/BottomNav';
import Footer from 'components/Footer';
import styles from 'ripple/Containers/Main/Main.scss';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AppDrawer from 'containers/Page/AppDrawer';
import { useApolloClient } from '@apollo/client';
import AppBar from '../AppBar';
import { GET_PROFILE_INFO } from '../../../schema/apolloQueries/commonQueries';
import { openAppStore, openPlayStore } from '../../../utils/helper';

// eslint-disable-next-line import/no-unresolved
const Activities = React.lazy(() => import('activities/Activities'));

function ElevationScroll({ children }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    rootClass: trigger ? 'elevatedAppBar' : 'transparentAppBar',
  });
}

ElevationScroll.propTypes = {
  children: PropTypes.element.isRequired,
};

const isDev = process.env.NODE_ENV === 'development';

const Main = ({
  children,
  loading,
  meta,
  relay,
  title,
  style,
  titleLink,
  trackerData,
  trackerTitle,
  padContent = true,
  ...props
}) => {
  const {
    match: {
      location: { pathname, query },
    },
  } = useRouter();
  const appStoreRef = useRef(null);
  const playStoreRef = useRef(null);
  const profileUpdate = useRef(null);
  const { showSignInDialog, setShowLoyaltyMessage } = useAuthContext();
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [profileUpdateRendered, setProfileUpdateRendered] = useState(null);

  const client = useApolloClient();

  const openNavigateToDownloadApp = useCallback(() => {
    if (playStoreRef?.current && appStoreRef?.current) {
      const isAndroid = /android/i.test(navigator.userAgent.toLowerCase());
      const isiOS = /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
      if (isAndroid) {
        playStoreRef.current.click();
      } else if (isiOS) {
        appStoreRef.current.click();
      }
    }
  }, [playStoreRef.current, appStoreRef.current]);

  useEffect(() => {
    if (query?.target === 'app_download') {
      openNavigateToDownloadApp();
    }
  }, [query]);

  const data = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const items = useMemo(
    () => [
        {
          key: 'discover',
          icon: <Explore />,
          label: 'Discover',
          path: '/discover/events',
        },
        {
          key: 'activities',
          skip: !data?.getProfile?.loggedIn,
          icon: <Assignment />,
          label: 'Activities',
          path: '/activities',
          badge: data?.getProfile?.loggedIn
            ? Number(data?.getProfile?.personInfo?.incompleteActivityCount)
              + Number(data?.getProfile?.personInfo?.incompleteInvitationCount)
              + Number(data?.getProfile?.personInfo?.incompletePartyCount)
              + Number(data?.getProfile?.personInfo?.pendingApplicationCount)
            : 0,
        },
        {
          key: 'profile',
          skip: !data?.getProfile?.loggedIn,
          icon: <Person />,
          label: 'Profile',
          path: '/profile',
        },
        // {
        //   key: 'loyalty',
        //   icon: <Loyalty />,
        //   label: 'Loyalty Program',
        //   // path: data?.getProfile?.loggedIn ? '/loyalty/rewards' : 'sign-in',
        //   handler: () => {
        //     if (!data?.getProfile?.loggedIn) {
        //       setShowLoyaltyMessage(true);
        //       showSignInDialog();
        //     }
        //   },
        // },
        {
          key: 'dashboard',
          skip: !data?.getProfile?.loggedIn,
          icon: <DashboardIcon />,
          label: 'Dashboard',
          path: '/dashboard',
        },
        {
          key: 'sign-in',
          skip: data?.getProfile?.loggedIn,
          icon: <ExitToApp />,
          label: 'Sign In',
          handler: () => {
            if (pathname !== '/sign-in') {
              setShowLoyaltyMessage(false);
              showSignInDialog();
            }
          },
        },
        {
          key: 'notifications',
          skip: !data?.getProfile?.loggedIn,
          icon: <Notifications />,
          label: 'Notifications',
          path: '/notifications',
          badge: data?.getProfile?.loggedIn
            ? Number(data?.getProfile?.totalNotificationCount)
            : 0,
        },
      ].filter(item => !item.skip),
    [
      pathname,
      setShowLoyaltyMessage,
      showSignInDialog,
      data?.getProfile?.loggedIn,
      data?.getProfile?.personInfo?.incompleteActivityCount,
      data?.getProfile?.personInfo?.incompleteInvitationCount,
      data?.getProfile?.personInfo?.incompletePartyCount,
      data?.getProfile?.personInfo?.pendingApplicationCount,
      data?.getProfile?.personInfo?.unreadNotificationCount,
    ],
  );
  /* eslint-disable */
  const drawerItems = useMemo(() => {
    const menus = [...items];
    if (data?.getProfile?.loggedIn) {
      menus.push({
        key: 'support',
        skip: !data?.getProfile?.loggedIn,
        icon: <Flag />,
        label: 'Contact Support',
        path: '/support',
      });
    }
    return menus.filter(item => !item.skip);
  }, [items, data?.getProfile?.loggedIn]);

  const closeLeftDrawer = useCallback(() => setLeftDrawerOpen(false), []);
  const openLeftDrawer = useCallback(() => setLeftDrawerOpen(true), []);

  const toggleLeftDrawer = useCallback(
    () => setLeftDrawerOpen(!leftDrawerOpen),
    [leftDrawerOpen]
  );

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0);
    }
    closeLeftDrawer();
  }, [
    closeLeftDrawer,
    pathname,
    title,
    trackerData,
    trackerTitle,
    data?.getProfile,
  ]);

  useEffect(() => {
    if (!isDev) {
      const allTrackerData = {
        ...trackerData,
        ...data?.getProfile,
      };
      tracker.trackFacebookPageView(allTrackerData);
      tracker.trackSnapPixelPageView(pathname, trackerTitle || strip(title));
      tracker.trackGoogleAnalyticsPageView(
        pathname,
        trackerTitle || strip(title),
        allTrackerData
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.getProfile?.loggedIn) {
      store.get('skipUntilNextLogin').then(value => {
        if (!value) {
          if (profileUpdate.current && !profileUpdateRendered) {
            setProfileUpdateRendered(
              <Suspense fallback="Loading Activities...">
                <Activities
                  context={{
                    userId: data?.getProfile?.personId,
                    targetPath: 'profile',
                    themeType: 'light',
                  }}
                />
              </Suspense>
            );
          }
          store.save('skipUntilNextLogin', true);
        }
      });
    }
  }, [profileUpdateRendered, data?.getProfile]);

  return (
    <Fragment>
      <NoSsr>
        {data?.getProfile?.personInfo && (
          <div ref={profileUpdate} style={{ margin: '0px 0 0px' }}>
            {profileUpdateRendered}
          </div>
        )}
      </NoSsr>
      <Helmet defaultTitle="Ripple Street" titleTemplate="%s | Ripple Street">
        <meta charSet="utf-8" />
        <meta property="fb:app_id" content={process.env.FACEBOOK_APP_ID} />
        <meta property="og:site_name" content="Ripple Street" />
        <meta property="og:url" content={`${process.env.APP_URL}${pathname}`} />
        {meta &&
          meta.map((prop, idx) => <meta {...prop} key={`meta-${idx}`} />)}
        <title>{strip(title)}</title>
        <style>
          {`#beacon-container{
              position: relative !important;
              z-index: 99999999 !important;
          }
            \ 
          @media only screen and (max-width: 600px) {\
            .BeaconFabButtonFrame {\
              bottom: 70px!important;\
            }\
            .BeaconContainer {\
              max-height: 450px!important;\
            }\
            .hsds-beacon .kSnNCl.is-mobile {\
              top: 100px!important;\
            }\
          }\
        `}
        </style>
        <script type="text/javascript">
          {`
        !function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});
        `}
        </script>
        <script type="text/javascript">
          {`
        window.Beacon('init', '3bcb0c39-cf4a-467b-9019-cbfd2a51f36c')
        `}
        </script>
        {/* <!-- Snap Pixel Code --> */}
        <script type="text/javascript">
          {`
          (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
            {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
            a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
            r.src=n;var u=t.getElementsByTagName(s)[0];
            u.parentNode.insertBefore(r,u);})(window,document,
            'https://sc-static.net/scevent.min.js');
            snaptr('init', '2c5e8f31-b478-413f-a50b-5c243ab4ac49', {
              'user_email': '${data?.getProfile?.personInfo?.email}'
            });
          `}
        </script>
        {/* <!-- End Snap Pixel Code --> */}
      </Helmet>
      <AcceptTermsDialog />
      <AppDrawer
        items={drawerItems}
        onClick={closeLeftDrawer}
        onRequestChange={toggleLeftDrawer}
        open={leftDrawerOpen}
      />
      <Container
        id="mainContainer"
        classes={{ root: styles.mainContainer }}
        maxWidth={false}
      >
        <NoSsr>
          <ElevationScroll {...props}>
            <AppBar
              closeLeftDrawer={closeLeftDrawer}
              openLeftDrawer={openLeftDrawer}
            />
          </ElevationScroll>
        </NoSsr>

        <BottomNav
          links={[
            {
              icon: <EventsIcon />,
              label: 'Events',
              path: '/discover/events',
            },
            {
              icon: <CommunitiesIcon />,
              label: 'Brands',
              path: '/discover/communities',
            },
            {
              icon: <ProductsIcon />,
              label: 'Products',
              path: '/discover/products',
            },
          ]}
        />
        <LoadBlur loading={loading}>
          <Box className={`${styles.mainBody} ${padContent && styles.withPad}`}>
            {children}
          </Box>
        </LoadBlur>
        <Footer />
        <div style={{ textAlign: 'center' }}>
          <img
            role='presentation'
            ref={appStoreRef}
            style={{
              display: 'none',
            }}
            alt='appstore'
            onClick={openAppStore}
          />
          <img
            role='presentation'
            ref={playStoreRef}
            style={{
              display: 'none',
            }}
            alt='playstore'
            onClick={openPlayStore}
          />
        </div>
      </Container>
    </Fragment>
  );
};

Main.propTypes = {
  children: PropTypes.node.isRequired,
  meta: PropTypes.array,
  relay: PropTypes.object.isRequired,
  style: PropTypes.object,
  title: PropTypes.string,
  titleLink: PropTypes.string,
  trackerData: PropTypes.object,
  trackerTitle: PropTypes.string,
  loading: PropTypes.bool,
  padContent: PropTypes.bool,
};

export default Main;
